import { useState, useRef, useMemo, useEffect } from 'react';
import Map, {
  Popup,
  NavigationControl,
  GeolocateControl,
  ScaleControl
} from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import mapboxgl from 'mapbox-gl';

// Fix for mapbox-gl worker
// @ts-ignore
mapboxgl.workerClass = require('mapbox-gl/dist/mapbox-gl-csp-worker').default;

import { useGetApp } from "@/context/GetAppContext";
import RiverDetailPanel from "@/components/RiverDetailPanel";

// Extracted popup components
import DestinationPopup from "@/components/map/DestinationPopup";
import GaugePopup from "@/components/map/GaugePopup";
import POIPopup from "@/components/map/POIPopup";
import WaterwayPopup from "@/components/map/WaterwayPopup";

// Extracted hooks
import useMapLayers from "@/hooks/useMapLayers";
import useMapInteractions from "@/hooks/useMapInteractions";
import useMapEffects from "@/hooks/useMapEffects";

// Extracted constants
import { POI_TYPE_COLORS, POI_ICON_MAP, sanitizeHTML, getTrendInfo, BASEMAPS } from "@/utils/mapConstants";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

import { MapFABMenu } from "@/components/map/MapFABMenu";
import { MapBasemapSelector } from "@/components/map/MapBasemapSelector";

export default function MapboxMap({
  destinations = [],
  selectedDestination,
  onDestinationSelect,
  onDestinationClick,
  onBoundsChange,
  onToggleDestinationFavorite,
  onWaterwayClick,
  initialCenter = [-98.5795, 39.8283],
  initialZoom = 4,
  flyToLocation = null,
  className = "",
  showStreamGauges = false,
  showRivers = false,
  showLakes = false,
  showPOIs = false,
  selectedPOITypes = []
}) {
  const mapRef = useRef(null);

  // Load saved basemap preference from localStorage, default to 'outdoors'
  const getSavedBasemap = () => {
    try {
      const saved = localStorage.getItem('diy_fishing_map_style');
      return saved && BASEMAPS[saved] ? saved : 'outdoors';
    } catch {
      return 'outdoors';
    }
  };

  const [basemap, setBasemap] = useState(getSavedBasemap);
  const [popupInfo, setPopupInfo] = useState(null);
  const [viewState, setViewState] = useState({
    longitude: initialCenter[0],
    latitude: initialCenter[1],
    zoom: initialZoom
  });
  const [mapLoaded, setMapLoaded] = useState(false);

  // River detail panel state
  const [riverPanelData, setRiverPanelData] = useState(null);

  // Dev-only: allow opening the river panel via `?debug_river=<gnis_id>` URL
  // query param so the SEC-001 gated tabs can be verified without needing to
  // click a specific river feature on the vector tile layer. Harmless when
  // the param is absent.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get('debug_river');
    if (q) {
      setRiverPanelData({
        gnisId: q,
        waterwayData: { type: 'river', name: 'Debug River', state: '' },
      });
    }
  }, []);

  const [fabOpen, setFabOpen] = useState(false);
  const openGetApp = useGetApp();

  const layers = useMapLayers();
  const { iconsLoaded, setIconsLoaded, loadIcons, updateLayers, updatePOILayer, updateWaterwaysLayer } = layers;

  const interactions = useMapInteractions({
    mapRef,
    destinations,
    onDestinationSelect,
    onWaterwayClick,
    showStreamGauges,
    showPOIs,
    selectedPOITypes,
    setPopupInfo,
    setRiverPanelData,
  });
  const { viewportPOIs, setViewportPOIs, riverDataLoading, fetchViewportData, onClick, onMouseEnter, onMouseLeave, handleMoveEnd, lastPOITypesRef } = interactions;

  // GeoJSON memos
  const destinationsGeoJSON = useMemo(() => ({
    type: 'FeatureCollection',
    features: destinations
      .filter(dest => dest.latitude && dest.longitude)
      .map((dest, index) => ({
        type: 'Feature',
        properties: {
          id: dest.id,
          name: dest.name,
          state: dest.state,
          difficulty: dest.difficulty,
          slug: dest.slug,
          target_species: dest.target_species,
          image_url: dest.image_url,
          sortOrder: index
        },
        geometry: {
          type: 'Point',
          coordinates: [dest.longitude, dest.latitude]
        }
      }))
  }), [destinations]);

  const poisGeoJSON = useMemo(() => ({
    type: 'FeatureCollection',
    features: viewportPOIs
      .filter(poi => poi.coordinates?.lat && poi.coordinates?.lng)
      .map(poi => ({
        type: 'Feature',
        properties: {
          id: poi.id,
          name: poi.name || poi.display_name,
          type: poi.type,
          source_id: poi.source_id,
          state: poi.state,
          county: poi.county,
          source: poi.source,
          description: poi.description,
          properties_json: JSON.stringify(poi.properties || {}),
          color: POI_TYPE_COLORS[poi.type] || POI_TYPE_COLORS.other,
          icon: POI_ICON_MAP[poi.type] || 'poi-other'
        },
        geometry: {
          type: 'Point',
          coordinates: [poi.coordinates.lng, poi.coordinates.lat]
        }
      }))
  }), [viewportPOIs]);

  // Coordinate all effects
  const effects = useMapEffects({
    mapRef,
    mapLoaded,
    iconsLoaded,
    setIconsLoaded,
    loadIcons,
    updateLayers,
    updatePOILayer,
    updateWaterwaysLayer,
    destinationsGeoJSON,
    poisGeoJSON,
    showStreamGauges,
    showPOIs,
    showRivers,
    showLakes,
    selectedPOITypes,
    fetchViewportData,
    setViewportPOIs,
    lastPOITypesRef,
    flyToLocation,
    onBoundsChange,
    handleMoveEnd,
  });
  const { handleMapLoad, onMoveEnd } = effects;

  // Save basemap preference
  const handleBasemapChange = (newBasemap) => {
    setBasemap(newBasemap);
    try {
      localStorage.setItem('diy_fishing_map_style', newBasemap);
    } catch (e) {
    }
  };

  // Open directions to a point
  const openDirections = (point) => {
    window.open(
      `https://www.google.com/maps/dir/?api=1&destination=${point.latitude},${point.longitude}`,
      '_blank'
    );
  };

  const currentBasemap = BASEMAPS[basemap];

  return (
    <div className={`relative ${className}`}>
      <Map
        ref={mapRef}
        {...viewState}
        onMove={evt => setViewState(evt.viewState)}
        onMoveEnd={onMoveEnd}
        onLoad={() => { setMapLoaded(true); handleMapLoad(); }}
        mapboxAccessToken={MAPBOX_TOKEN}
        mapStyle={currentBasemap.url}
        projection="mercator"
        style={{ width: '100%', height: '100%' }}
        interactiveLayerIds={['destinations-layer', 'pois-layer', 'pois-circles', 'waterways-flowlines', 'waterways-waterbodies']}
        onClick={(e) => {
          setFabOpen(false);
          onClick(e);
        }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {/* Popup */}
        {popupInfo && (
          <Popup
            longitude={popupInfo.longitude}
            latitude={popupInfo.latitude}
            anchor="bottom"
            onClose={() => setPopupInfo(null)}
            closeButton={false}
            closeOnClick={false}
            className="mapbox-popup-styled"
            maxWidth="320px"
          >
            {popupInfo.destination ? (
              <DestinationPopup
                destination={popupInfo.destination}
                onClose={() => setPopupInfo(null)}
                onToggleFavorite={onToggleDestinationFavorite}
                onViewDetails={onDestinationClick}
                onDirections={openDirections}
              />
            ) : popupInfo.streamGauge ? (
              <GaugePopup
                gauge={popupInfo.streamGauge}
                getTrendInfo={getTrendInfo}
                onClose={() => setPopupInfo(null)}
                onSave={() => openGetApp("Saving stream gauges")}
              />
            ) : popupInfo.poi ? (
              <POIPopup
                poi={popupInfo.poi}
                poiTypeColors={POI_TYPE_COLORS}
                sanitizeHTML={sanitizeHTML}
                onClose={() => setPopupInfo(null)}
              />
            ) : popupInfo.waterway ? (
              <WaterwayPopup
                waterway={popupInfo.waterway}
                riverDataLoading={riverDataLoading}
                onClose={() => {
                  setPopupInfo(null);
                  if (mapRef.current) {
                    const map = mapRef.current.getMap();
                    if (map.getLayer('waterway-highlight')) {
                      map.setFilter('waterway-highlight', ['==', 'Permanent_Identifier', '']);
                    }
                  }
                }}
              />
            ) : null}
          </Popup>
        )}

        {/* Controls */}
        <ScaleControl unit="imperial" position="bottom-left" />
        <GeolocateControl
          position="bottom-right"
          trackUserLocation={true}
          showUserHeading={true}
        />
        <NavigationControl position="bottom-right" />
      </Map>

      {/* Relocate and placement banners + FAB button */}
      <MapFABMenu fabOpen={fabOpen} setFabOpen={setFabOpen} onGetApp={openGetApp} />

      {/* Basemap Selector */}
      <MapBasemapSelector basemaps={BASEMAPS} basemap={basemap} onBasemapChange={handleBasemapChange} />

      {/* River Detail Bottom Sheet */}
      {riverPanelData && (
        <RiverDetailPanel
          gnisId={riverPanelData.gnisId}
          waterwayData={riverPanelData.waterwayData}
          onClose={() => setRiverPanelData(null)}
          onPoiClick={(poi) => {
            if (poi.coordinates?.lat && poi.coordinates?.lng) {
              mapRef.current?.flyTo({
                center: [poi.coordinates.lng, poi.coordinates.lat],
                zoom: Math.max(mapRef.current.getZoom(), 14),
                duration: 800,
              });
              setRiverPanelData(null);
              setPopupInfo({
                longitude: poi.coordinates.lng,
                latitude: poi.coordinates.lat,
                poi: {
                  ...poi,
                  type: poi.type,
                }
              });
            }
          }}
        />
      )}

    </div>
  );
}
